import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import styles from "./ProductBarcode.module.scss";
import primaryComponents from "../../primaryComponents";
import Icon from "../../../assets/icons";
import Barcode from "react-barcode";
import { formatCurrency } from "../../../helpers";
import { useReactToPrint } from "react-to-print";
import { useRef, useState } from "react";
import html2canvas from "html2canvas";
import useProductBarcode from "./useProductBarcode";
import { useLabelPrint } from "../../../hooks/useLabelPrint";
import secondaryComponents from "..";
import { useSelector } from "react-redux";

const ProductBarcode = ({
  closeWidget,
  selectedProduct,
  handleFetchproducts,
}) => {
  const {
    isLoading,
    barCodeProperties,
    isRegenratePrompt,
    createBarcode,
    setIsRegenratePrompt,
  } = useProductBarcode({
    selectedProduct,
    handleFetchproducts,
  });
  const isVariantProduct = selectedProduct?.variants?.length > 0;
  const barCodesArray = isVariantProduct
    ? barCodeProperties
    : [barCodeProperties];
  const nonVariantComponent = useRef();
  const boxRefs = useRef([]);
  const [isPrintingLabels, setIsPrintingLabels] = useState(false);
  const handlePrint = useReactToPrint({
    content: () => nonVariantComponent.current,
  });
  const { isLabelAgentReady, printLabel } = useLabelPrint();
  const { currency_code } = useSelector(
    (state) => state.profileDetailsReducer.businessDetails,
  );

  // Sends one already-rendered .barcodeBox node to the label agent — no
  // OS print dialog, since PrintDocument.Print() never shows one. Resolves
  // once that single label has actually been sent, so the caller can print
  // several labels one at a time rather than firing them all at once.
  const printBoxViaAgent = async (boxEl) => {
    const canvas = await html2canvas(boxEl, {
      backgroundColor: "#ffffff",
      scale: 2,
      useCORS: true,
      logging: false,
    });
    const imageData = canvas.toDataURL("image/png");

    return new Promise((resolve, reject) => {
      printLabel({
        imageData,
        onSuccess: resolve,
        onError: () => reject(new Error("Label agent failed to print a label")),
      });
    });
  };

  const handlePrintClick = async () => {
    const agentReady = await isLabelAgentReady();
    if (!agentReady) return handlePrint();

    setIsPrintingLabels(true);
    try {
      const boxes = boxRefs.current.filter(Boolean);
      for (const boxEl of boxes) {
        // eslint-disable-next-line no-await-in-loop
        await printBoxViaAgent(boxEl);
      }
    } catch {
      // Agent reachable but a print call failed mid-run (e.g. printer went
      // offline) — fall back to the browser dialog for the whole batch
      // rather than leaving the merchant with a partially-printed set.
      handlePrint();
    } finally {
      setIsPrintingLabels(false);
    }
  };

  return (
    <>
      <div className={styles.barcodeDisplay}>
        <div className="mb-3">
          <FontAwesomeIcon
            onClick={closeWidget}
            icon="angle-left"
            style={{ fontSize: "25px", cursor: "pointer" }}
          />
        </div>
        <h4>Barcode</h4>
        <p className="mb-4">
          Print generated barcode(s). <br />
          Generated barcode(s) are automatically recorded in the system
        </p>

        <secondaryComponents.LoaderHelper
          isLoading={isLoading}
          classNames="mt-5"
        >
          <div className={styles.barcodeCover} ref={nonVariantComponent}>
            {barCodesArray?.map((item, index) => (
              <div
                key={index}
                className={styles.barcodeBox}
                ref={(el) => (boxRefs.current[index] = el)}
              >
                <div className={styles.textwithbackground}>
                  <h5>
                    {selectedProduct?.name}{" "}
                    {isVariantProduct ? `(${item?.name})` : ""}
                  </h5>
                </div>
                <div className={`${styles.textwithbackground} mt-2 mb-2`}>
                  <h5 className="mb-0">
                    {isVariantProduct
                      ? formatCurrency({
                          value:
                            selectedProduct?.variants?.[index]
                              ?.store_variant_properties?.[0]?.pricings?.[0]
                              ?.price,
                          currencyCode: currency_code,
                        })
                      : formatCurrency({
                          value:
                            selectedProduct?.store_product_properties?.[0]
                              ?.pricings?.[0]?.price || 0,
                          currencyCode: currency_code,
                        })}
                  </h5>
                </div>

                <Barcode
                  value={item?.barcode}
                  height={50}
                  width={2}
                  fontSize={16}
                  margin={2}
                />
              </div>
            ))}
          </div>
        </secondaryComponents.LoaderHelper>

        <div className={styles.bottomSection}>
          {/* print button */}
          <primaryComponents.Button
            classNames="btn btn--primary mx-auto cover"
            onClick={handlePrintClick}
            isDisabled={isLoading || isPrintingLabels}
            isLoading={isPrintingLabels}
            loadingText="Printing..."
          >
            <img src={Icon.printWhiteIcon} className="mr-2" alt="" />
            Print
          </primaryComponents.Button>
          {/* generate barcode */}
          <primaryComponents.Button
            classNames="btn btn--outline mx-auto cover"
            onClick={() => setIsRegenratePrompt(true)}
            isDisabled={isLoading}
            isLoading={isLoading}
            loadingText="Regenerating..."
          >
            Regenerate Barcode
          </primaryComponents.Button>
        </div>
      </div>

      {/* regenrate confir,ation */}
      <secondaryComponents.Modal
        isActive={isRegenratePrompt}
        closeModal={() => setIsRegenratePrompt(false)}
        width="500px"
      >
        <div className="py-3 px-3">
          <h4 className="mb-3 text-center">Confirm Barcode Regeneration</h4>
          <div
            style={{
              backgroundColor: "#F3F1FC",
              padding: "16px",
              display: "flex",
              flexDirection: "column",
              gap: "20px",
              borderRadius: "8px",
            }}
          >
            <p>
              Kindly be aware that regenerating the barcode will require
              updating all products associated with the current barcode using
              the newly generated one. Are you sure you want to proceed with the
              barcode regeneration
            </p>
          </div>

          <div
            className="d-flex justify-content-between mt-4"
            style={{ gap: "20px" }}
          >
            <primaryComponents.Button
              classNames="btn btn--outline cover"
              onClick={() => setIsRegenratePrompt(false)}
            >
              Cancel
            </primaryComponents.Button>
            <primaryComponents.Button
              classNames="btn btn--primary cover"
              onClick={() => {
                createBarcode();
                setIsRegenratePrompt(false);
              }}
            >
              Continue
            </primaryComponents.Button>
          </div>
        </div>
      </secondaryComponents.Modal>
    </>
  );
};
export default ProductBarcode;
